import React, { useState, useEffect } from 'react';
import s from './OnboardingPage.module.css';
import IconLogo from '../../../shared/ui/icons/IconLogo';

import Button from '../../../shared/ui/button/Button';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';

import LoadingGeneration from '../../../shared/ui/loading-generation/LoadingGeneration';
import { useSelector } from 'react-redux';
import { selectToken } from '../../../store/selectors/authSelectors';
import onboarding_image1 from '../../../assets/img/step1.svg';
import onboarding_image2 from '../../../assets/img/step2.svg';
import IconArrowRight from '../../../shared/ui/icons/IconArrowRight';
import SelectPage from './SelectPage';
import { fetchSocialPages } from '../../../shared/api/socialApi';
import { RootState, useAppDispatch } from '../../../store/store';
import { selectProviderPages } from '../../../store/selectors/socialSelectors';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
};

const OnboardingPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState(1);
    const [selectedSocial, setSelectedSocial] = useState('');
    const [selectedSocialPage, setSelectedSocialPage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const token = useSelector(selectToken);
    const dispatch = useAppDispatch();
    const isLoading = false;
    const isMobile = useIsMobile();

    const providerPages = useSelector((state: RootState) =>
        selectProviderPages(state, selectedSocial),
    );

    useEffect(() => {
        const urlStep = searchParams.get('step');
        const urlError = searchParams.get('error');

        const urlProvider = searchParams.get('provider');
        if (urlProvider) setSelectedSocial(urlProvider);

        if (urlStep) {
            setStep(parseInt(urlStep));
        }

        if (urlError) {
            setError(urlError);
            setStep(2);
        }

        if (urlStep && parseInt(urlStep) == 3 && !providerPages?.length && urlProvider) {
            dispatch(fetchSocialPages(urlProvider));
        }
    }, [searchParams]);

    const handleConnect = () => {
        const apiUrl = process.env.REACT_APP_API_URL;
        let redirectUri = `${window.location.origin}/generate-quick-post/${selectedSocial}?source=onboarding`;

        if (['facebook', 'linkedin', 'pinterest'].includes(selectedSocial)) {
            redirectUri = `${window.location.origin}/onboarding?step=3&provider=${selectedSocial}`;
        }

        const authUrl = `${apiUrl}/auth/${selectedSocial}?user_token=${token}&link_account=true&redirect_uri=${encodeURIComponent(`${redirectUri}`)}`;
        window.location.href = authUrl;
    };

    const handleQuickPost = () => {
        const path = `/generate-quick-post/${selectedSocial}?source=onboarding&page_id=${selectedSocialPage}`;
        navigate(path);
    };

    const renderImage = () => {
        const imageSrc = step === 1 ? onboarding_image1 : onboarding_image2;
        const altText = `onboarding step ${step}`;

        return (
            <img
                src={imageSrc}
                alt={altText}
                className={s.onboarding_image}
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
            />
        );
    };

    if (isLoading) {
        return <LoadingGeneration />;
    } else {
        return (
            <div className={s.container_wrapper}>
                <div className={s.logo_container}>
                    <IconLogo />
                    <p>SimpleeCreate</p>
                </div>
                <div className={s.container}>
                    {isMobile ? (
                        <div className={s.mobile_container}>
                            <div className={s.mobile_content}>
                                {step === 1 ? (
                                    <div>
                                        <h1 className={s.title}>Let's make your first post!</h1>
                                        <p className={s.subtitle}>
                                            We'll help you get started quickly with a great-looking,
                                            on-brand post
                                        </p>
                                    </div>
                                ) : step === 2 ? (
                                    <div>
                                        <h1 className={s.title}>Connect your social account</h1>
                                        <p className={s.subtitle}>
                                            Let's link to your main channel so we can preview,
                                            optimize, and create your content seamlessly!
                                        </p>
                                        {error && (
                                            <div className={s.errorMessage}>
                                                <p>
                                                    Error:{' '}
                                                    {error === 'twitter_linked'
                                                        ? 'Twitter account already linked'
                                                        : error}
                                                </p>
                                            </div>
                                        )}
                                        <div className={s.social_connect_container}>
                                            <div className={s.socialGrid}>
                                                <label className={s.socialOption}>
                                                    <input
                                                        type="radio"
                                                        value="instagram"
                                                        name="social"
                                                        onChange={(e) =>
                                                            setSelectedSocial(e.target.value)
                                                        }
                                                    />
                                                    <span>Instagram</span>
                                                </label>
                                                <label className={s.socialOption}>
                                                    <input
                                                        type="radio"
                                                        value="linkedin"
                                                        name="social"
                                                        onChange={(e) =>
                                                            setSelectedSocial(e.target.value)
                                                        }
                                                    />
                                                    <span>LinkedIn</span>
                                                </label>
                                                <label className={s.socialOption}>
                                                    <input
                                                        type="radio"
                                                        value="twitter"
                                                        name="social"
                                                        onChange={(e) =>
                                                            setSelectedSocial(e.target.value)
                                                        }
                                                    />
                                                    <span>X</span>
                                                </label>
                                                <label className={s.socialOption}>
                                                    <input
                                                        type="radio"
                                                        value="tiktok"
                                                        name="social"
                                                        onChange={(e) =>
                                                            setSelectedSocial(e.target.value)
                                                        }
                                                    />
                                                    <span>TikTok</span>
                                                </label>
                                                <label className={s.socialOption}>
                                                    <input
                                                        type="radio"
                                                        value="youtube"
                                                        name="social"
                                                        onChange={(e) =>
                                                            setSelectedSocial(e.target.value)
                                                        }
                                                    />
                                                    <span>Youtube</span>
                                                </label>
                                                <label className={s.socialOption}>
                                                    <input
                                                        type="radio"
                                                        value="pinterest"
                                                        name="social"
                                                        onChange={(e) =>
                                                            setSelectedSocial(e.target.value)
                                                        }
                                                    />
                                                    <span>Pinterest</span>
                                                </label>
                                                <label className={s.socialOption}>
                                                    <input
                                                        type="radio"
                                                        value="facebook"
                                                        name="social"
                                                        onChange={(e) =>
                                                            setSelectedSocial(e.target.value)
                                                        }
                                                    />
                                                    <span>Facebook</span>
                                                </label>
                                                <label className={s.socialOption}>
                                                    <input
                                                        type="radio"
                                                        value="threads"
                                                        name="social"
                                                        onChange={(e) =>
                                                            setSelectedSocial(e.target.value)
                                                        }
                                                    />
                                                    <span>Threads</span>
                                                </label>
                                            </div>
                                        </div>
                                        <div className={s.warning}>
                                            <span className={s.lockIcon}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="22"
                                                    height="22"
                                                    viewBox="0 0 22 22"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M16.5147 8.6604V6.0645C16.5147 3.02485 14.0434 0.549805 11 0.549805C7.9603 0.549805 5.48526 3.02485 5.48526 6.0645V8.6604C4.21291 8.76673 3.21191 9.84106 3.21191 11.139V14.2338C3.21191 18.2121 6.44587 21.4498 10.4242 21.4498H11.5755C15.5539 21.4498 18.7878 18.2121 18.7878 14.2338V11.139C18.7878 9.84097 17.787 8.7667 16.5147 8.6604ZM7.8723 6.0645C8.04372 1.92027 13.9563 1.9212 14.1276 6.0645V8.6496H7.8723L7.8723 6.0645ZM10.1273 15.3376C8.84031 14.498 9.46823 12.4456 11 12.4592C12.5318 12.4464 13.1606 14.497 11.8727 15.3376C11.7737 15.4036 11.7114 15.521 11.7114 15.642V16.929C11.6875 17.8658 10.3134 17.8677 10.2887 16.929V15.642C10.2887 15.521 10.2263 15.4037 10.1273 15.3376Z"
                                                        fill="#707070"
                                                    />
                                                </svg>
                                            </span>
                                            <span className={s.subtitle}>
                                                We will never post without your permission,you
                                                control
                                                <br />
                                                what gets published and when.
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <h1 className={s.title}>Add your social account page</h1>
                                        <p className={s.subtitle}>
                                            Connect your social page to manage all your content from
                                            one hub. This allows us to tailor your experience and
                                            keep your posts aligned across every platform.
                                        </p>
                                        {error && (
                                            <div className={s.errorMessage}>
                                                <p>
                                                    Error:{' '}
                                                    {error === 'twitter_linked'
                                                        ? 'Twitter account already linked'
                                                        : error}
                                                </p>
                                            </div>
                                        )}
                                        <div style={{ padding: '1rem 0', width: '100%' }}>
                                            <SelectPage
                                                selectedSocial={selectedSocial}
                                                onChange={(opt) => setSelectedSocialPage(opt.value)}
                                                source="scrapping"
                                            />
                                        </div>
                                        <div className={s.warning}>
                                            <span className={s.lockIcon}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="22"
                                                    height="22"
                                                    viewBox="0 0 22 22"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M16.5147 8.6604V6.0645C16.5147 3.02485 14.0434 0.549805 11 0.549805C7.9603 0.549805 5.48526 3.02485 5.48526 6.0645V8.6604C4.21291 8.76673 3.21191 9.84106 3.21191 11.139V14.2338C3.21191 18.2121 6.44587 21.4498 10.4242 21.4498H11.5755C15.5539 21.4498 18.7878 18.2121 18.7878 14.2338V11.139C18.7878 9.84097 17.787 8.7667 16.5147 8.6604ZM7.8723 6.0645C8.04372 1.92027 13.9563 1.9212 14.1276 6.0645V8.6496H7.8723L7.8723 6.0645ZM10.1273 15.3376C8.84031 14.498 9.46823 12.4456 11 12.4592C12.5318 12.4464 13.1606 14.497 11.8727 15.3376C11.7737 15.4036 11.7114 15.521 11.7114 15.642V16.929C11.6875 17.8658 10.3134 17.8677 10.2887 16.929V15.642C10.2887 15.521 10.2263 15.4037 10.1273 15.3376Z"
                                                        fill="#707070"
                                                    />
                                                </svg>
                                            </span>
                                            <span className={s.subtitle}>
                                                We will never post without your permission,you
                                                control
                                                <br />
                                                what gets published and when.
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={s.mobile_image_container}>{renderImage()}</div>

                            <div className={s.mobile_buttons}>
                                {step === 1 ? (
                                    <>
                                        <p className={s.subtitle}>Don't worry — we'll guide you.</p>
                                        <Button
                                            text="Continue"
                                            icon={<IconArrowRight />}
                                            iconPosition="right"
                                            onClick={() => {
                                                setStep(2);
                                                const newSearchParams = new URLSearchParams(
                                                    searchParams,
                                                );
                                                newSearchParams.set('step', '2');
                                                navigate(
                                                    `/onboarding?${newSearchParams.toString()}`,
                                                    { replace: true },
                                                );
                                            }}
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                backgroundColor: '#FFDD35',
                                                color: '#000',
                                                marginTop: '20px',
                                            }}
                                        />
                                    </>
                                ) : step === 2 ? (
                                    <div className={s.buttonContainer}>
                                        <Button
                                            text="Back"
                                            onClick={() => {
                                                setStep(1);
                                                const newSearchParams = new URLSearchParams(
                                                    searchParams,
                                                );
                                                newSearchParams.set('step', '1');
                                                navigate(
                                                    `/onboarding?${newSearchParams.toString()}`,
                                                    { replace: true },
                                                );
                                            }}
                                            style={{
                                                width: '48%',
                                                height: '40px',
                                                backgroundColor: '#f5f5f5',
                                                color: '#23262a',
                                            }}
                                        />
                                        <Button
                                            text="Continue"
                                            icon={<IconArrowRight />}
                                            iconPosition="right"
                                            onClick={handleConnect}
                                            state={!selectedSocial ? 'disabled' : 'enabled'}
                                            style={{
                                                width: '48%',
                                                height: '40px',
                                                backgroundColor: '#FFDD35',
                                                color: '#000',
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className={s.buttonContainer}>
                                        <Button
                                            text="Back"
                                            onClick={() => {
                                                setStep(1);
                                                const urlStep = parseInt(
                                                    searchParams.get('step') ?? '1',
                                                    10,
                                                );
                                                const newSearchParams = new URLSearchParams(
                                                    searchParams.toString(),
                                                );
                                                newSearchParams.set('step', String(urlStep - 1));
                                                navigate(
                                                    `/onboarding?${newSearchParams.toString()}`,
                                                    { replace: true },
                                                );
                                            }}
                                            style={{
                                                width: '48%',
                                                height: '40px',
                                                backgroundColor: 'var(--third-color)',
                                                color: 'var(--primary-color)',
                                            }}
                                        />
                                        <Button
                                            text="Continue"
                                            icon={<IconArrowRight />}
                                            iconPosition="right"
                                            onClick={step === 3 ? handleQuickPost : handleConnect}
                                            state={!selectedSocialPage ? 'disabled' : 'enabled'}
                                            style={{
                                                width: '48%',
                                                height: '40px',
                                                backgroundColor: '#FFDD35',
                                                color: '#000',
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        // Десктопная версия (оригинальная структура)
                        <>
                            <div className={s.left_side}>
                                <div className={s.left_side_content}>
                                    {step === 1 ? (
                                        <>
                                            <div>
                                                <h1 className={s.title}>
                                                    Let's make your first post!
                                                </h1>
                                                <p className={s.subtitle}>
                                                    We'll help you get started quickly with a
                                                    great-looking, on-brand post
                                                </p>
                                            </div>

                                            <div style={{ width: '100%' }}>
                                                <p
                                                    className={s.subtitle}
                                                    style={{ marginTop: '40vh' }}
                                                >
                                                    Don't worry — we'll guide you.
                                                </p>
                                                <Button
                                                    text="Continue"
                                                    icon={<IconArrowRight />}
                                                    iconPosition="right"
                                                    onClick={() => {
                                                        setStep(2);
                                                        const newSearchParams = new URLSearchParams(
                                                            searchParams,
                                                        );
                                                        newSearchParams.set('step', '2');
                                                        navigate(
                                                            `/onboarding?${newSearchParams.toString()}`,
                                                            { replace: true },
                                                        );
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        height: '40px',
                                                        backgroundColor: '#FFDD35',
                                                        color: '#000',
                                                        marginTop: '20px',
                                                    }}
                                                />
                                            </div>
                                        </>
                                    ) : step === 2 ? (
                                        <motion.div
                                            key={`step-${step}`}
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className={s.social_connect}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <div>
                                                <div>
                                                    <h1 className={s.title}>
                                                        Connect your social account
                                                    </h1>
                                                    <p className={s.subtitle}>
                                                        Let's link to your main channel so we can
                                                        preview, optimize, and create your content
                                                        seamlessly!
                                                    </p>

                                                    {error && (
                                                        <div className={s.errorMessage}>
                                                            <p>
                                                                Error:{' '}
                                                                {error === 'twitter_linked'
                                                                    ? 'Twitter account already linked'
                                                                    : error}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={s.social_connect_container}>
                                                    <div className={s.socialGrid}>
                                                        <label className={s.socialOption}>
                                                            <input
                                                                type="radio"
                                                                value="instagram"
                                                                name="social"
                                                                onChange={(e) =>
                                                                    setSelectedSocial(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span>Instagram</span>
                                                        </label>
                                                        <label className={s.socialOption}>
                                                            <input
                                                                type="radio"
                                                                value="linkedin"
                                                                name="social"
                                                                onChange={(e) =>
                                                                    setSelectedSocial(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span>LinkedIn</span>
                                                        </label>
                                                        <label className={s.socialOption}>
                                                            <input
                                                                type="radio"
                                                                value="twitter"
                                                                name="social"
                                                                onChange={(e) =>
                                                                    setSelectedSocial(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span>X</span>
                                                        </label>
                                                        <label className={s.socialOption}>
                                                            <input
                                                                type="radio"
                                                                value="tiktok"
                                                                name="social"
                                                                onChange={(e) =>
                                                                    setSelectedSocial(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span>TikTok</span>
                                                        </label>
                                                        <label className={s.socialOption}>
                                                            <input
                                                                type="radio"
                                                                value="youtube"
                                                                name="social"
                                                                onChange={(e) =>
                                                                    setSelectedSocial(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span>Youtube</span>
                                                        </label>
                                                        <label className={s.socialOption}>
                                                            <input
                                                                type="radio"
                                                                value="pinterest"
                                                                name="social"
                                                                onChange={(e) =>
                                                                    setSelectedSocial(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span>Pinterest</span>
                                                        </label>
                                                        <label className={s.socialOption}>
                                                            <input
                                                                type="radio"
                                                                value="facebook"
                                                                name="social"
                                                                onChange={(e) =>
                                                                    setSelectedSocial(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span>Facebook</span>
                                                        </label>
                                                        <label className={s.socialOption}>
                                                            <input
                                                                type="radio"
                                                                value="threads"
                                                                name="social"
                                                                onChange={(e) =>
                                                                    setSelectedSocial(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span>Threads</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className={s.warning}>
                                                    <span className={s.lockIcon}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="22"
                                                            height="22"
                                                            viewBox="0 0 22 22"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M16.5147 8.6604V6.0645C16.5147 3.02485 14.0434 0.549805 11 0.549805C7.9603 0.549805 5.48526 3.02485 5.48526 6.0645V8.6604C4.21291 8.76673 3.21191 9.84106 3.21191 11.139V14.2338C3.21191 18.2121 6.44587 21.4498 10.4242 21.4498H11.5755C15.5539 21.4498 18.7878 18.2121 18.7878 14.2338V11.139C18.7878 9.84097 17.787 8.7667 16.5147 8.6604ZM7.8723 6.0645C8.04372 1.92027 13.9563 1.9212 14.1276 6.0645V8.6496H7.8723L7.8723 6.0645ZM10.1273 15.3376C8.84031 14.498 9.46823 12.4456 11 12.4592C12.5318 12.4464 13.1606 14.497 11.8727 15.3376C11.7737 15.4036 11.7114 15.521 11.7114 15.642V16.929C11.6875 17.8658 10.3134 17.8677 10.2887 16.929V15.642C10.2887 15.521 10.2263 15.4037 10.1273 15.3376Z"
                                                                fill="#707070"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span className={s.subtitle}>
                                                        We will never post without your
                                                        permission,you control
                                                        <br />
                                                        what gets published and when.
                                                    </span>
                                                </div>
                                            </div>

                                            <div className={s.buttonContainer}>
                                                <Button
                                                    text="Back"
                                                    onClick={() => {
                                                        setStep(1);
                                                        const newSearchParams = new URLSearchParams(
                                                            searchParams,
                                                        );
                                                        newSearchParams.set('step', '1');
                                                        navigate(
                                                            `/onboarding?${newSearchParams.toString()}`,
                                                            { replace: true },
                                                        );
                                                    }}
                                                    style={{
                                                        width: '48%',
                                                        height: '40px',
                                                        backgroundColor: '#f5f5f5',
                                                        color: '#23262a',
                                                    }}
                                                />
                                                <Button
                                                    text="Continue"
                                                    icon={<IconArrowRight />}
                                                    iconPosition="right"
                                                    onClick={handleConnect}
                                                    state={!selectedSocial ? 'disabled' : 'enabled'}
                                                    style={{
                                                        width: '48%',
                                                        height: '40px',
                                                        backgroundColor: '#FFDD35',
                                                        color: '#000',
                                                    }}
                                                />
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key={`step-${step}`}
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className={s.social_connect}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <div>
                                                <div>
                                                    <h1 className={s.title}>
                                                        Add your social account page
                                                    </h1>
                                                    <p className={s.subtitle}>
                                                        Connect your social page to manage all your
                                                        content from one hub. This allows us to
                                                        tailor your experience and keep your posts
                                                        aligned across every platform.
                                                    </p>

                                                    {error && (
                                                        <div className={s.errorMessage}>
                                                            <p>
                                                                Error:{' '}
                                                                {error === 'twitter_linked'
                                                                    ? 'Twitter account already linked'
                                                                    : error}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{ padding: '1rem 0', width: '100%' }}>
                                                    <SelectPage
                                                        selectedSocial={selectedSocial}
                                                        onChange={(opt) =>
                                                            setSelectedSocialPage(opt.value)
                                                        }
                                                        source="scrapping"
                                                    />
                                                </div>

                                                <div className={s.warning}>
                                                    <span className={s.lockIcon}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="22"
                                                            height="22"
                                                            viewBox="0 0 22 22"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M16.5147 8.6604V6.0645C16.5147 3.02485 14.0434 0.549805 11 0.549805C7.9603 0.549805 5.48526 3.02485 5.48526 6.0645V8.6604C4.21291 8.76673 3.21191 9.84106 3.21191 11.139V14.2338C3.21191 18.2121 6.44587 21.4498 10.4242 21.4498H11.5755C15.5539 21.4498 18.7878 18.2121 18.7878 14.2338V11.139C18.7878 9.84097 17.787 8.7667 16.5147 8.6604ZM7.8723 6.0645C8.04372 1.92027 13.9563 1.9212 14.1276 6.0645V8.6496H7.8723L7.8723 6.0645ZM10.1273 15.3376C8.84031 14.498 9.46823 12.4456 11 12.4592C12.5318 12.4464 13.1606 14.497 11.8727 15.3376C11.7737 15.4036 11.7114 15.521 11.7114 15.642V16.929C11.6875 17.8658 10.3134 17.8677 10.2887 16.929V15.642C10.2887 15.521 10.2263 15.4037 10.1273 15.3376Z"
                                                                fill="#707070"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span className={s.subtitle}>
                                                        We will never post without your
                                                        permission,you control
                                                        <br />
                                                        what gets published and when.
                                                    </span>
                                                </div>
                                            </div>

                                            <div className={s.buttonContainer}>
                                                <Button
                                                    text="Back"
                                                    onClick={() => {
                                                        setStep(1);
                                                        const urlStep = parseInt(
                                                            searchParams.get('step') ?? '1',
                                                            10,
                                                        );
                                                        const newSearchParams = new URLSearchParams(
                                                            searchParams.toString(),
                                                        );
                                                        newSearchParams.set(
                                                            'step',
                                                            String(urlStep - 1),
                                                        );
                                                        navigate(
                                                            `/onboarding?${newSearchParams.toString()}`,
                                                            { replace: true },
                                                        );
                                                    }}
                                                    style={{
                                                        width: '48%',
                                                        height: '40px',
                                                        backgroundColor: '#f5f5f5',
                                                        color: '#23262a',
                                                    }}
                                                />
                                                <Button
                                                    text="Continue"
                                                    icon={<IconArrowRight />}
                                                    iconPosition="right"
                                                    onClick={
                                                        step === 3 ? handleQuickPost : handleConnect
                                                    }
                                                    state={
                                                        !selectedSocialPage ? 'disabled' : 'enabled'
                                                    }
                                                    style={{
                                                        width: '48%',
                                                        height: '40px',
                                                        backgroundColor: '#FFDD35',
                                                        color: '#000',
                                                    }}
                                                />
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                            <div className={s.right_side}>{renderImage()}</div>
                        </>
                    )}
                </div>
            </div>
        );
    }
};

export default OnboardingPage;
